import { isNetworkError } from './offline'

/**
 * 1.65 — how the till's needs leave the device (the rules; the device side is
 * `lib/need-recorder.ts`). The `cart-removal-queue.ts` pattern, with one
 * difference Codex's design review (#4) asked for: a database that does not
 * have the table yet is „not yet", never „drop" — the cashier was told the
 * need is on record.
 *
 * - Every row carries the till's own id: a batch resent after a lost answer is
 *   ignored by the database (ON CONFLICT DO NOTHING), never doubled.
 * - Every row remembers the shop and the user it was recorded under (on the
 *   device only): after someone else signs in here, it is not sent under them.
 * - A row the database refuses for its own content is found and dropped alone,
 *   so one bad row cannot jam the queue. The stamp (191) nulls a vanished
 *   product, shift or branch instead of refusing, so this is rare by design.
 */

export type QueuedNeed = {
  id: string
  product_id: string | null
  name: string
  qty: number
  contact: string | null
  note: string | null
  branch_id: string | null
  shift_id: string | null
  // kept on the device only
  _org: string
  _uid: string
}

export type NeedRow = Omit<QueuedNeed, '_org' | '_uid'>

type Refusal = { code?: string; status?: number; message?: string } | null | undefined

export const NEED_BATCH = 50

/** 'retry' keeps the row for the next flush; 'drop' lets it go (with a console line). */
export function refusalKind(err: Refusal): 'retry' | 'drop' {
  if (!err) return 'drop'
  if (isNetworkError(err)) return 'retry'
  if (err.code === 'PGRST205' || err.code === '42P01' || err.status === 404) return 'retry'
  return 'drop'
}

const toRow = ({ _org, _uid, ...row }: QueuedNeed): NeedRow => row

/**
 * Sends this shop's and this user's queued needs in batches. Returns the ids
 * that are done with (sent, or dropped for their own content) and whether it
 * stopped early on a refusal worth retrying.
 */
export async function sendNeeds(
  queue: QueuedNeed[],
  org: string,
  uid: string,
  send: (rows: NeedRow[]) => Promise<{ error: Refusal | unknown }>,
): Promise<{ done: Set<string>; stopped: boolean }> {
  const done = new Set<string>()
  const mine = queue.filter((r) => r._org === org && r._uid === uid)
  for (let i = 0; i < mine.length; i += NEED_BATCH) {
    const batch = mine.slice(i, i + NEED_BATCH)
    const { error } = await send(batch.map(toRow))
    if (!error) { batch.forEach((r) => done.add(r.id)); continue }
    if (refusalKind(error as Refusal) === 'retry') return { done, stopped: true }
    // refused for a reason of its own: find the row, keep the rest
    for (const r of batch) {
      const { error: one } = await send([toRow(r)])
      if (one && refusalKind(one as Refusal) === 'retry') return { done, stopped: true }
      if (one) console.error('need refused', one, r)
      done.add(r.id)
    }
  }
  return { done, stopped: false }
}

/**
 * Where a new need lands (Codex's final review #5): the device's queue when it can keep it; when it cannot (storage
 * blocked or full), straight to the server; „failed" when neither holds it — the till must not say „ჩაიწერა" then.
 */
export async function placeNeed(
  row: QueuedNeed,
  persist: (row: QueuedNeed) => boolean,
  send: (rows: NeedRow[]) => Promise<{ error: Refusal | unknown }>,
): Promise<'queued' | 'sent' | 'failed'> {
  if (persist(row)) return 'queued'
  const { error } = await send([toRow(row)])
  return error ? 'failed' : 'sent'
}

/** One slice's reads: only the newest request's answer may land (Codex's final review #2). */
export function latestOnly() {
  let n = 0
  return { next: () => ++n, isLatest: (k: number) => k === n }
}
